import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  Grid,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  addShopToShopOwner,
  updateShopLocation,
} from '../../middleware/registration_service/updateRequest';
import { getGeoCode } from '../../middleware/registration_service/getRequests';
import { parseGeocodeResponse } from '../../utils/geocodingUtils';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import { UK_CITIES } from '../../defaults/Cities';
import { Shop } from '../../types/shop';
import headerLogo from '../../images/header-logo.png';
import {
  darkPageBg,
  darkFormContainerSx,
  darkTextFieldSx,
  darkSelectSx,
  darkSelectMenuProps,
  darkButtonSx,
} from '../components/darkFormStyles';

const ShopRegistration = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: '',
    addressLineOne: '',
    addressLineTwo: '',
    city: '',
    country: 'United Kingdom',
    postcode: '',
    email: '',
    openingHours: '',
    closingHours: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCityChange = (e: SelectChangeEvent) => {
    setForm({ ...form, city: e.target.value });
  };

  const handleHoursChange = (e: SelectChangeEvent) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const HOURS = Array.from(
    { length: 24 },
    (_, i) => `${String(i).padStart(2, '0')}:00`
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const shopOwnerIdStr = localStorage.getItem('reg_shop_owner_id');
    if (!shopOwnerIdStr) {
      setError('Shop Owner ID not found. Please start registration again.');
      return;
    }

    if (
      !form.name ||
      !form.addressLineOne ||
      !form.city ||
      !form.postcode ||
      !form.openingHours ||
      !form.closingHours
    ) {
      setError('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    try {
      // Sanitize: strip any non-digit characters before parsing
      const shopOwnerId = shopOwnerIdStr.replace(/\D/g, '');
      if (!shopOwnerId) {
        setError('Invalid Shop Owner ID. Please start registration again.');
        setLoading(false);
        return;
      }

      const shopData: Shop = {
        name: form.name,
        addressLineOne: form.addressLineOne,
        addressLineTwo: form.addressLineTwo,
        city: form.city,
        country: form.country,
        postcode: form.postcode,
        email: form.email,
        openingHours: form.openingHours,
        closingHours: form.closingHours,
        shopOwnerId: BigInt(shopOwnerId),
      };

      const res = await addShopToShopOwner(shopData, BigInt(shopOwnerId));
      const shops: Shop[] = res.data?.shops ?? [];
      const newShop = shops[shops.length - 1];
      const shopId: bigint | undefined =
        newShop?.id != null ? newShop.id : undefined;

      if (!shopId) {
        setError('Failed to retrieve Shop ID. Please try again.');
        setLoading(false);
        return;
      }

      localStorage.setItem('reg_shop_id', shopId.toString());

      // Geocode the shop's address and persist coordinates (best-effort;
      // a geocoding failure should not block registration).
      try {
        const address = [
          form.addressLineOne,
          form.addressLineTwo,
          form.city,
          form.postcode,
          form.country,
        ]
          .filter(Boolean)
          .join(', ');
        const geocodeRes = await getGeoCode(address);
        const coordinates = parseGeocodeResponse(geocodeRes.data);
        if (coordinates) {
          await updateShopLocation(shopId, coordinates.lat, coordinates.lng);
        } else {
          console.warn('Could not resolve coordinates for shop address.');
        }
      } catch (geoErr) {
        console.error('Failed to geocode/save shop location:', geoErr);
      }

      navigate('/register/shop-barbers');
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'An error occurred while creating the shop. Please try again.'
      );
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: darkPageBg, minHeight: '100vh', py: 8 }}>
      <Container
        component="main"
        maxWidth="sm"
        sx={{ ...darkFormContainerSx, mt: 4, mb: 4, p: 4 }}
      >
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={headerLogo}
            alt="Barbers Limited"
            sx={{ width: 88, height: 88, objectFit: 'contain', mb: 1 }}
          />
          <Typography
            component="h1"
            variant="h5"
            fontWeight="bold"
            sx={{ color: '#fff' }}
          >
            Shop Registration
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 1, textAlign: 'center', color: 'rgba(255,255,255,0.65)' }}
          >
            Step 2 of 3 — Set up your shop
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}

          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{ mt: 3, width: '100%' }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Shop Name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  autoFocus
                  sx={darkTextFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Address Line 1"
                  name="addressLineOne"
                  value={form.addressLineOne}
                  onChange={handleChange}
                  sx={darkTextFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  name="addressLineTwo"
                  value={form.addressLineTwo}
                  onChange={handleChange}
                  sx={darkTextFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required sx={darkSelectSx}>
                  <InputLabel id="city-label">City</InputLabel>
                  <Select
                    labelId="city-label"
                    value={form.city}
                    label="City"
                    onChange={handleCityChange}
                    MenuProps={darkSelectMenuProps}
                  >
                    {UK_CITIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  InputProps={{ readOnly: true }}
                  sx={darkTextFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Post Code"
                  name="postcode"
                  value={form.postcode}
                  onChange={handleChange}
                  sx={darkTextFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  sx={darkTextFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required sx={darkSelectSx}>
                  <InputLabel id="opening-hours-label">Opening Hour</InputLabel>
                  <Select
                    labelId="opening-hours-label"
                    name="openingHours"
                    value={form.openingHours}
                    label="Opening Hour"
                    onChange={handleHoursChange}
                    MenuProps={darkSelectMenuProps}
                    required
                  >
                    {HOURS.map((h) => (
                      <MenuItem key={h} value={h}>
                        {h}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required sx={darkSelectSx}>
                  <InputLabel id="closing-hours-label">Closing Hour</InputLabel>
                  <Select
                    labelId="closing-hours-label"
                    name="closingHours"
                    value={form.closingHours}
                    label="Closing Hour"
                    onChange={handleHoursChange}
                    MenuProps={darkSelectMenuProps}
                    required
                  >
                    {HOURS.map((h) => (
                      <MenuItem key={h} value={h}>
                        {h}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, mb: 2, py: 1.5, ...darkButtonSx }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Continue to Add Barbers'
              )}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ShopRegistration;
